/**
 * BasecampSectionView
 *
 * Full-panel main-content view for Basecamp top-level sections:
 *   Bookmarks | My Stuff | Todos | Projects
 *
 * Rendered by BasecampDashboard when initialData.section is set.
 * Todos use shared TodoCard + Reports API; My Stuff is a compact summary + link.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  RefreshCw,
  AlertCircle,
  Loader2,
  User2,
  ExternalLink,
  Pin,
  Clock,
  ArrowUp,
  ArrowDown,
  Bookmark,
  Eye,
  FolderOpen,
  Check,
  ArrowUpDown,
  Minus,
  AppWindow,
  X,
  SlidersHorizontal,
  ChevronDown,
  Settings,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Panel, PanelGroup } from 'react-resizable-panels';
import {
  useBasecampProjects,
  useBasecampAssignments,
  useBasecampAuth,
  ensureTodoListsCached,
  isTodoListsFetchInflight,
  subscribeTodoListsInflight,
} from '@/hooks/use-basecamp'
import { useBookmarksData } from './components/bookmarks-widget';
import { openWorkspaceExtensionSettings } from '@/lib/open-settings';
import {
  ExtensionFilterBar,
  ExtensionListRow,
  ExtensionPanePlaceholder,
  ExtensionPaneRetryButton,
  IndexPreviewEmpty,
  IndexSurfaceShell,
  SecondaryPreviewResizeHandle,
  useRegisterIndexPreviewPin,
} from '@/components/integrations/_shared';
import { ListViewModeToggle, type ListViewMode } from '@/components/integrations/_shared/list-view-mode-toggle';
import { ListSurfaceStatusBar } from '@/components/ui/list-surface-status-bar';
import { LIST_GESTURE_TIPS_WITH_PREVIEW } from '@/lib/list-gesture-tips';
import { isModClick } from '@/lib/open-intent';
import { TodosAssigneeCombobox } from './todos-assignee-combobox';
import { TodosFiltersMenu } from './todos-filters-menu';
import { PanelSearchBar } from '@/components/ui/panel-search-bar';
import { usePanelSearch } from '@/hooks/use-panel-search';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { StorageKeys, StorageSchemas } from '@/lib/storage';
import {
  describeTodosViewFilters,
  migrateLegacyTodosView,
  todosViewFromSearchParams,
  type TodosListMode,
  type TodosViewState,
} from '@/lib/basecamp-view-persist';
import { appendQuery, isBasecampTodosPath, todosQueryFromView } from '@/lib/index-query-url';
import { TodoCard } from './todo-card';
import { BasecampTodoPreviewPane } from './basecamp-todo-preview-pane';
import { BasecampProjectPreviewPane } from './basecamp-project-preview-pane';
import { BasecampDueBadge } from './components/basecamp-due-badge';
import { buildTodosRowContextMenuEntries } from './basecamp-todos-row-context-menu';
import { panelSearchMenuAction } from '@/lib/panel-search-menu';
import {
  INDEX_VIEW_ACTION_EVENT,
  indexViewActionFromEvent,
} from '@/lib/index-surface-actions';
import { EntityContextMenu } from '@/components/features/entity-context-menu';
import { testIds } from '@/lib/test-ids';
import {
  BOOKMARKS_PROJECT_FOCUS_EVENT,
  loadBookmarksProjectFocus,
  setBookmarksProjectFocus,
  type BookmarksProjectFocus,
} from '@/lib/basecamp-tab-routing';
import { resolveBasecampTodoIngestUrlFromTodo } from '@/components/layout/document-viewer.utils';
import { fetchWithAuth } from '@/lib/api';
import { useAuth } from '@/providers/auth-provider';
import { toast } from 'sonner';
import {
  hydrateMapsFromAssignments,
  useTodosColumnEnrich,
  applyWorkspecMerge,
  applyCommentMerge,
} from './hooks/use-todos-column-enrich';
import { MyStuffSummary } from './my-stuff-summary';
import { cn, parseLocalDate } from '@/lib/utils';
import {
  isBasecampDueOnOrBefore,
  isBasecampDueOverdue,
  isBasecampDueToday,
  todayInLosAngeles,
} from '@/lib/basecamp-calendar';
import { QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS } from '@/lib/menu-chrome';
import type { BasecampAssignment, BasecampProject } from '@/types/basecamp';
import {
  assignmentMatchesBasecampQuery,
  buildAssignmentDisplayRows,
  type AssignmentDisplayRow,
} from '@/lib/basecamp-search-match';
import {
  inferMeFromAssignments,
  mergeMeIdentity,
  type AssigneeFacet,
  type MeIdentity,
  formatAssigneesLabel,
  groupAssigneeNames,
  rowMatchesAssigneeFacet,
} from '@/lib/basecamp-assignee';
import { basecampApi } from '@/lib/basecamp-api';
import {
  formatRelativeSyncLabel,
  loadProjectsPanelUiState,
  loadProjectsSortMode,
  loadProjectPreviewLoadedMap,
  loadProjectTouches,
  orderProjects,
  PROJECTS_PREVIEW_OPEN_KEY,
  PROJECTS_RELEVANCE_CAP,
  PROJECTS_SORT_LABELS,
  PROJECTS_SORT_MODES,
  recordProjectPreviewLoaded,
  recordProjectTouch,
  reconcilePreviewLoadedMap,
  rememberProjectsPanelSelection,
  resolveProjectCacheDepth,
  saveProjectPreviewLoadedMap,
  saveProjectsPanelUiState,
  saveProjectsSortMode,
  shouldClearStaleProjectsSelection,
  type ProjectCacheDepth,
  type ProjectsSortMode,
} from '@/lib/basecamp-project-relevance';

type Section = 'bookmarks' | 'mystuff' | 'todos' | 'projects'

interface BasecampSectionViewProps {
  section: Section
  /** Workspace organization UUID (scopes Basecamp API when set). */
  organizationId?: string
}

// ─── helpers ────────────────────────────────────────────────────────────────

function openTodo(
  assignment: BasecampAssignment,
  organizationId?: string,
  options?: { highlightStepId?: string },
  openInNewTab?: boolean,
) {
  const isSubtask = assignment._recording_type === 'Kanban::Step';
  const parentTodoId = isSubtask && assignment._parent_todo_id
    ? assignment._parent_todo_id
    : assignment.id.toString();
  const projectId =
    assignment.project?.id || assignment._basecamp_project_id;
  if (!projectId) return;
  window.dispatchEvent(
    new CustomEvent('integration-view-change', {
      detail: {
        tabId: `basecamp-todo-${parentTodoId}`,
        title: isSubtask ? (assignment._parent_title || 'Loading...') : assignment.title,
        type: 'integration-basecamp-todo',
        openInNewTab,
        integrationData: {
          integrationId: 'basecamp',
          projectId: projectId.toString(),
          todoId: parentTodoId,
          projectName:
            assignment.project?.name ||
            assignment._project_name ||
            'Unknown Project',
          ...(isSubtask
            ? { highlightStepId: options?.highlightStepId ?? assignment.id.toString() }
            : {}),
          ...(organizationId ? { organizationId } : {}),
        },
      },
    })
  );
}

function openProject(project: BasecampProject, organizationId?: string) {
  if (project.id != null) {
    recordProjectTouch(project.id);
    rememberProjectsPanelSelection(project.id, organizationId);
  }
  window.dispatchEvent(
    new CustomEvent('integration-view-change', {
      detail: {
        tabId: `basecamp-project-${project.id}`,
        title: project.name,
        type: 'integration-basecamp',
        integrationData: {
          integrationId: 'basecamp',
          projectId: project.id?.toString(),
          projectName: project.name,
          ...(organizationId ? { organizationId } : {}),
        },
      },
    })
  );
}

function openBookmarksSection(
  organizationId?: string,
  project?: { id: string; name: string },
) {
  setBookmarksProjectFocus(project ? { id: project.id, name: project.name } : null);
  window.dispatchEvent(
    new CustomEvent('integration-view-change', {
      detail: {
        tabId: 'basecamp-bookmarks',
        title: 'Bookmarks',
        type: 'integration-basecamp',
        integrationData: {
          integrationId: 'basecamp',
          section: 'bookmarks',
          ...(organizationId ? { organizationId } : {}),
        },
      },
    }),
  );
}

type Scope = 'hop_only' | 'client_only' | 'all';
type StatusFilter = 'all' | 'overdue' | 'due_today' | 'overdue_and_today';

function isHopInternal(a: BasecampAssignment): boolean {
  const name = (a._project_name ?? a.project?.name ?? '').toLowerCase();
  const title = (a.title ?? '').toLowerCase();
  const parent = (a._parent_title ?? '').toLowerCase();
  return (
    name.includes('hop business') ||
    name.includes('~hop') ||
    name.startsWith('hop ') ||
    title.startsWith('hop ') ||
    title.includes('in-hop') ||
    parent.includes('hop')
  );
}

function filterByScope(list: BasecampAssignment[], scope: Scope): BasecampAssignment[] {
  if (scope === 'all') return list;
  if (scope === 'hop_only') return list.filter(isHopInternal);
  return list.filter((a) => !isHopInternal(a));
}

function filterByStatus(list: BasecampAssignment[], status: StatusFilter): BasecampAssignment[] {
  if (status === 'overdue') return list.filter((a) => isBasecampDueOverdue(a.due_on));
  if (status === 'due_today') return list.filter((a) => isBasecampDueToday(a.due_on));
  if (status === 'overdue_and_today') return list.filter((a) => isBasecampDueOnOrBefore(a.due_on));
  return list;
}

// ─── Section header ──────────────────────────────────────────────────────────

function SectionHeader({
  icon: Icon,
  title,
  count,
  onRefresh,
  refreshing,
  children,
}: {
  icon: React.ElementType
  title: string
  count?: number
  onRefresh?: () => void
  refreshing?: boolean
  children?: React.ReactNode
}) {
  return (
    <div className="border-b border-border bg-muted/20 px-6 py-4 flex items-center gap-3 flex-shrink-0">
      <Icon className="w-5 h-5 text-muted-foreground" />
      <h1 className="text-lg font-semibold">{title}</h1>
      {count !== undefined && count > 0 && (
        <Badge variant="secondary">{count}</Badge>
      )}
      <div className="flex-1" />
      {children}
      {onRefresh && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={refreshing}
          className="gap-1.5 text-muted-foreground"
        >
          <RefreshCw className={cn('w-3.5 h-3.5', refreshing && 'animate-spin')} />
          Refresh
        </Button>
      )}
    </div>
  );
}

// ─── Empty / loading / error states ──────────────────────────────────────────

function LoadingState({ label }: { label: string }) {
  return (
    <ExtensionPanePlaceholder
      variant="loading"
      title={label}
      testId={testIds.basecamp.panePlaceholder}
    />
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string
  onRetry?: () => void
}) {
  return (
    <ExtensionPanePlaceholder
      variant="error"
      title={message}
      testId={testIds.basecamp.panePlaceholder}
      action={onRetry ? <ExtensionPaneRetryButton onClick={onRetry} /> : undefined}
    />
  );
}

export function ConnectState({
  authUrl,
  organizationId,
}: {
  authUrl?: string
  organizationId?: string
}) {
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    if (!isConnecting) return;
    const onSuccess = () => setIsConnecting(false);
    window.addEventListener('basecamp-auth-success', onSuccess);
    return () => window.removeEventListener('basecamp-auth-success', onSuccess);
  }, [isConnecting]);

  const openOAuth = () => {
    window.open(authUrl || '/auth/basecamp', 'basecamp-auth', 'width=600,height=700');
    setIsConnecting(true);
  };

  if (isConnecting) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-xs">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm font-medium mb-1">Waiting for authorization...</p>
          <p className="text-xs text-muted-foreground mb-4">Complete the sign-in in the popup window.</p>
          <Button size="sm" variant="ghost" onClick={() => setIsConnecting(false)}>Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <ExtensionPanePlaceholder
      variant="connect"
      title="Basecamp is disconnected"
      description="This workspace is not signed in. Saved todos stay in Maater, and this list stays blank until you connect."
      testId={testIds.basecamp.panePlaceholder}
      action={
        <div className="flex items-center justify-center gap-2">
          <Button size="sm" onClick={openOAuth}>
            Connect Basecamp
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid={testIds.basecampSidebar.paneSettings}
            onClick={() => openWorkspaceExtensionSettings('basecamp', organizationId)}
          >
            <Settings className="mr-1.5 h-3.5 w-3.5" />
            Settings
          </Button>
        </div>
      }
    />
  );
}

function EmptyState({ label, sub }: { label: string; sub?: string }) {
  return (
    <ExtensionPanePlaceholder
      variant="empty"
      title={label}
      description={sub}
      testId={testIds.basecamp.panePlaceholder}
    />
  );
}

// ─── Bookmarks panel ──────────────────────────────────────────────────────────

const BOOKMARKS_PREVIEW_OPEN_KEY = 'flownotes-bookmarks-preview-open';

/** Teach via (i) tooltip - not a permanent banner (maater-ui#list-status-chrome). */
const PROJECTS_MEMBERSHIP_HELP =
  'Your Basecamp membership. This list only includes projects your OAuth user can see. Cache depth is org-shared for those projects - not a full-account dump for everyone.';

type ProjectCacheDepthFilter = 'all' | ProjectCacheDepth;
type ProjectsViewSyncFrame = 'idle' | 'refreshing-visible' | 'done';

function projectCacheDepth(project: BasecampProject): ProjectCacheDepth {
  const id = project.id?.toString() ?? '';
  if (!id) return 'none';
  return resolveProjectCacheDepth({
    cacheDepth: project.cacheDepth,
    listsFresh: project.listsFresh,
    listsCachedAt: project.listsCachedAt,
    lastSync: project.lastSync,
  });
}

type ListsDepthOverride = {
  cacheDepth: ProjectCacheDepth;
  listsCachedAt: string;
  listsFresh: boolean;
};

function applyListsDepthOverride(
  project: BasecampProject,
  overrides: Record<string, ListsDepthOverride>,
): BasecampProject {
  const id = project.id?.toString() ?? '';
  const o = id ? overrides[id] : undefined;
  if (!o) return project;
  return {
    ...project,
    cacheDepth: o.cacheDepth,
    listsCachedAt: o.listsCachedAt,
    listsFresh: o.listsFresh,
  };
}

/** MaaterSegmentControl language in production tokens (maater-ui active segment). */
function CacheDepthFilterTrack({
  value,
  onChange,
  counts,
}: {
  value: ProjectCacheDepthFilter;
  onChange: (next: ProjectCacheDepthFilter) => void;
  counts: { all: number; ok: number; partial: number; none: number };
}) {
  const options: { id: ProjectCacheDepthFilter; label: string }[] = [
    { id: 'all', label: `All (${counts.all})` },
    { id: 'ok', label: `Ok (${counts.ok})` },
    { id: 'partial', label: `Partial (${counts.partial})` },
    { id: 'none', label: `None (${counts.none})` },
  ];
  return (
    <div
      className="inline-flex flex-wrap gap-1 rounded-[10px] bg-muted p-[3px]"
      data-testid={testIds.basecamp.projectsDepthFilter}
      role="tablist"
      aria-label="Filter by cache depth"
    >
      {options.map((opt) => {
        const active = opt.id === value;
        return (
          <button
            key={opt.id}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={testIds.basecamp.projectsDepthFilterOption(opt.id)}
            onClick={() => onChange(opt.id)}
            className={cn(
              'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
              active
                ? 'border-primary bg-background text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function ProjectCacheDepthBadge({
  depth,
  busy,
}: {
  depth: ProjectCacheDepth;
  busy?: boolean;
}) {
  if (busy) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin text-primary" />
        Refreshing
      </span>
    );
  }
  if (depth === 'ok') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-600 dark:text-green-500">
        <Check className="h-3 w-3" strokeWidth={2.5} />
        Ok
      </span>
    );
  }
  if (depth === 'partial') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 dark:text-amber-500">
        <Minus className="h-3 w-3" />
        Partial
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
      <span className="inline-block h-1.5 w-1.5 rounded-full border border-muted-foreground/50" />
      None
    </span>
  );
}

function formatBookmarkDue(assignment: BasecampAssignment): string | null {
  if (!assignment.due_on) return null;
  const dueDate = parseLocalDate(assignment.due_on);
  const todayStart = parseLocalDate(todayInLosAngeles());
  const diffDays = Math.round((dueDate.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24));
  if (isBasecampDueOverdue(assignment.due_on)) return `${Math.abs(diffDays)}d overdue`;
  if (diffDays === 0) return 'Due today';
  if (diffDays === 1) return 'Due tomorrow';
  if (diffDays <= 7) return `Due in ${diffDays}d`;
  return dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function assignmentProjectId(assignment: BasecampAssignment): string {
  return String(
    assignment.project?.id ??
      assignment._basecamp_project_id ??
      assignment.project_id ??
      assignment.bucket_id ??
      '',
  );
}

function BookmarksPanel({ organizationId }: { organizationId?: string }) {
  const {
    authStatus,
    bookmarks,
    bookmarksLoading,
    bookmarksError,
    loadBookmarks,
  } = useBookmarksData(organizationId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ListViewMode>('list');
  const [previewOpen, setPreviewOpen] = useState(() => {
    try {
      return localStorage.getItem(BOOKMARKS_PREVIEW_OPEN_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    assignment: BasecampAssignment;
  } | null>(null);
  const [projectFocus, setProjectFocus] = useState<BookmarksProjectFocus | null>(
    () => loadBookmarksProjectFocus(),
  );
  const {
    searchOpen,
    searchQuery: bookmarkQuery,
    setSearchQuery: setBookmarkQuery,
    inputRef: bookmarksSearchRef,
    close: closeBookmarksSearch,
    open: openBookmarksSearch,
  } = usePanelSearch();

  useEffect(() => {
    const onFocus = (e: Event) => {
      const detail = (e as CustomEvent<BookmarksProjectFocus | null>).detail;
      setProjectFocus(detail ?? null);
    };
    window.addEventListener(BOOKMARKS_PROJECT_FOCUS_EVENT, onFocus);
    return () => window.removeEventListener(BOOKMARKS_PROJECT_FOCUS_EVENT, onFocus);
  }, []);

  useEffect(() => {
    const onRefresh = (e: Event) => {
      const section = (e as CustomEvent).detail?.section;
      if (section === 'bookmarks') window.dispatchEvent(new CustomEvent('basecamp-bookmarks-reload'));
    };
    window.addEventListener('basecamp-section-refresh', onRefresh);
    return () => window.removeEventListener('basecamp-section-refresh', onRefresh);
  }, []);

  const visibleBookmarks = (projectFocus
    ? bookmarks.filter((b) => assignmentProjectId(b) === projectFocus.id)
    : bookmarks
  ).filter((b) => {
    const q = bookmarkQuery.trim().toLowerCase()
    if (!q) return true
    const hay = `${b.title || ''} ${b._project_name || ''} ${b.project?.name || ''} ${b._parent_title || ''}`.toLowerCase()
    return hay.includes(q)
  });
  const selected = bookmarks.find((b) => b.id.toString() === selectedId) ?? null;

  useEffect(() => {
    if (selectedId && !visibleBookmarks.some((b) => b.id.toString() === selectedId)) {
      setSelectedId(null);
    }
  }, [visibleBookmarks, selectedId]);

  const openBookmark = useCallback(
    (assignment: BasecampAssignment, event?: { metaKey?: boolean; ctrlKey?: boolean }) =>
      openTodo(assignment, organizationId, undefined, isModClick(event)),
    [organizationId],
  );

  const togglePreview = useCallback(() => {
    setPreviewOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(BOOKMARKS_PREVIEW_OPEN_KEY, String(next));
      } catch {
        /* ignore */
      }
      if (next && !selectedId && visibleBookmarks[0]) {
        setSelectedId(visibleBookmarks[0].id.toString());
      }
      return next;
    });
  }, [selectedId, visibleBookmarks]);

  useRegisterIndexPreviewPin({
    open: previewOpen,
    onToggle: togglePreview,
    testId: testIds.basecamp.bookmarksPreviewToggle,
  });

  if (authStatus && !authStatus.authenticated) {
    return (
      <div className="relative h-full">
        <ConnectState authUrl={authStatus.auth_url} organizationId={organizationId} />
      </div>
    );
  }

  return (
    <IndexSurfaceShell
      data-testid={testIds.basecamp.bookmarksPanel}
      search={
        searchOpen ? (
          <PanelSearchBar
            inputRef={bookmarksSearchRef}
            value={bookmarkQuery}
            onChange={setBookmarkQuery}
            onClose={closeBookmarksSearch}
            placeholder="filter bookmarks..."
            resultCount={visibleBookmarks.length}
            totalCount={bookmarks.length}
            countNoun="bookmarks"
          />
        ) : null
      }
      footer={
        <ListSurfaceStatusBar
          includePreviewTip
          trailing={
            <span>
              {visibleBookmarks.length} bookmark{visibleBookmarks.length === 1 ? '' : 's'}
            </span>
          }
        />
      }
    >
      <PanelGroup direction="horizontal" className="flex min-h-0 flex-1">
        <Panel minSize={35} className="flex min-h-0 min-w-0 flex-col overflow-hidden">
          <ExtensionFilterBar
            singleRow
            search=""
            onSearchChange={() => undefined}
            showSearch={false}
            trailing={
              <>
                {projectFocus ? (
                  <button
                    type="button"
                    onClick={() => setBookmarksProjectFocus(null)}
                    className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                    data-testid={testIds.basecamp.bookmarksProjectFilter}
                  >
                    <Bookmark className="h-3 w-3" />
                    {projectFocus.name}
                    <X className="h-3 w-3 opacity-70" />
                  </button>
                ) : (
                  <p className="truncate text-xs text-muted-foreground">
                    Pinned todos you care about
                  </p>
                )}
                <span className="text-[11px] tabular-nums text-muted-foreground">
                  {visibleBookmarks.length} shown
                </span>
                <ListViewModeToggle
                  value={viewMode}
                  onChange={setViewMode}
                  testId={testIds.basecamp.bookmarksViewMode}
                />
              </>
            }
          />
          <div className="min-h-0 flex-1 overflow-auto">
          {bookmarksLoading && bookmarks.length === 0 ? (
            <LoadingState label="Loading bookmarks..." />
          ) : bookmarksError ? (
            <ErrorState message={bookmarksError} onRetry={() => loadBookmarks(true)} />
          ) : bookmarks.length === 0 ? (
            <EmptyState
              label="No bookmarked items"
              sub="Bookmark from a todo, or refresh to discover bookmarks set on basecamp.com."
            />
          ) : visibleBookmarks.length === 0 ? (
            <EmptyState
              label={`No bookmarks in ${projectFocus?.name ?? 'this project'}`}
              sub="Clear the project filter to see all bookmarks."
            />
          ) : (
            <ul className={viewMode === 'grid' ? 'grid grid-cols-2 gap-2 p-3' : viewMode === 'card' ? 'flex flex-col gap-2 p-3' : undefined}>
              {visibleBookmarks.map((bookmark) => {
                const id = bookmark.id.toString();
                const dueLabel = formatBookmarkDue(bookmark);
                const listName = bookmark._parent_title ?? '';
                const projectName =
                  bookmark._project_name ?? bookmark.project?.name ?? 'Unknown project';
                return (
                  <li key={id}>
                    <ExtensionListRow
                      selected={selectedId === id}
                      testId={testIds.basecamp.bookmarksRow(id)}
                      onSelect={() => setSelectedId(id)}
                      onOpen={(event) => openBookmark(bookmark, event)}
                      onContextMenu={(e) => {
                        setSelectedId(id);
                        setContextMenu({ x: e.clientX, y: e.clientY, assignment: bookmark });
                      }}
                    >
                      <Bookmark className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{bookmark.title}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {projectName}
                          {listName ? (
                            <>
                              <span className="mx-1.5 opacity-40">·</span>
                              {listName}
                            </>
                          ) : null}
                        </div>
                      </div>
                      {dueLabel ? (
                        <BasecampDueBadge
                          dueOn={bookmark.due_on}
                          isOverdue={bookmark._is_overdue}
                          className="shrink-0"
                        />
                      ) : null}
                    </ExtensionListRow>
                  </li>
                );
              })}
            </ul>
          )}
          </div>
        </Panel>

        {previewOpen ? (
          <>
            <SecondaryPreviewResizeHandle />
            <Panel
              defaultSize={32}
              minSize={22}
              maxSize={45}
              className="flex min-h-0 flex-col overflow-hidden"
              data-testid={testIds.basecamp.bookmarksSecondary}
            >
              {selected ? (
                <BasecampTodoPreviewPane
                  headerTestId={testIds.basecamp.bookmarksPreviewHeader}
                  assignment={selected}
                  onOpen={(event) => openBookmark(selected, event)}
                  onClose={() => {
                    setPreviewOpen(false);
                    try {
                      localStorage.setItem(BOOKMARKS_PREVIEW_OPEN_KEY, 'false');
                    } catch {
                      /* ignore */
                    }
                  }}
                  onOpenBasecamp={(url) => window.open(url, '_blank', 'noopener,noreferrer')}
                />
              ) : (
                <IndexPreviewEmpty
                  noun="bookmark"
                  row
                  headerTestId={testIds.basecamp.bookmarksPreviewHeader}
                  testId={testIds.basecamp.bookmarksPreviewEmpty}
                  onClose={() => {
                    setPreviewOpen(false);
                    try {
                      localStorage.setItem(BOOKMARKS_PREVIEW_OPEN_KEY, 'false');
                    } catch {
                      /* ignore */
                    }
                  }}
                />
              )}
            </Panel>
          </>
        ) : null}
      </PanelGroup>

      {contextMenu ? (
        <EntityContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          data-testid={testIds.basecamp.bookmarksRowContextMenu}
          onClose={() => setContextMenu(null)}
          entries={[
            {
              label: 'Preview',
              icon: Eye,
              onSelect: () => {
                setSelectedId(contextMenu.assignment.id.toString());
                setPreviewOpen(true);
                try {
                  localStorage.setItem(BOOKMARKS_PREVIEW_OPEN_KEY, 'true');
                } catch {
                  /* ignore */
                }
              },
            },
            {
              label: 'Open',
              icon: ExternalLink,
              onSelect: () => openBookmark(contextMenu.assignment),
            },
            { separator: true as const },
            panelSearchMenuAction(openBookmarksSearch),
          ]}
        />
      ) : null}
    </IndexSurfaceShell>
  );
}

// ─── My Stuff panel (compact summary + link to Todos) ─────────────────────────

function MyStuffPanel({ organizationId }: { organizationId?: string }) {
  const { assignments, isLoading, error, refetch, cachedAt } =
    useBasecampAssignments(false, organizationId);

  return (
    <div className="h-full flex flex-col">
      <SectionHeader
        icon={User2}
        title="My Stuff"
        count={assignments?.length ?? 0}
        onRefresh={() => refetch(true)}
        refreshing={isLoading}
      />
      <div className="flex-1 flex items-center justify-center">
        <MyStuffSummary
          assignments={assignments}
          isLoading={isLoading}
          error={error}
          cachedAt={cachedAt}
          organizationId={organizationId}
          onRefresh={() => refetch(true)}
          variant="panel"
        />
      </div>
    </div>
  );
}

// ─── Todos table header + grid ──────────────────────────────────────────────

// Sentinel passed to TodoCard to activate table-row rendering (value is truthy, not used as CSS).
const TODOS_GRID_TEMPLATE = 'table';
// Fixed column widths (px): stripe | project | list | assignee | spec | activity | due | actions
// Title column is auto (fills remaining space). Min table width = fixed cols + 200px min title.
const TODOS_FIXED_COL_WIDTHS = [2, 110, 130, 110, 80, 200, 130, 40] as const;
const TODOS_MIN_WIDTH = TODOS_FIXED_COL_WIDTHS.reduce((a, b) => a + b, 0) + 200;
const TODOS_PAGE_SIZE = 80;

type TodoSortColumn = 'project' | 'title' | 'list' | 'assignee' | 'due' | 'activity';
type TodoSortDir = 'asc' | 'desc';
interface TodoSortState { column: TodoSortColumn; dir: TodoSortDir; }

function SortIndicator({ column, sort }: { column: TodoSortColumn; sort: TodoSortState | null }) {
  if (!sort || sort.column !== column) return null;
  return sort.dir === 'asc'
    ? <ArrowUp className="w-3 h-3 inline-block ml-0.5 text-foreground/60" />
    : <ArrowDown className="w-3 h-3 inline-block ml-0.5 text-foreground/60" />;
}

function TodosTableHeader({ sort, onSort }: { sort: TodoSortState | null; onSort: (col: TodoSortColumn) => void }) {
  const thCls = 'border-b border-border text-left font-medium h-8';
  const btnCls = 'px-2 h-full w-full truncate cursor-pointer hover:text-foreground transition-colors flex items-center gap-0.5';
  return (
    <thead className="sticky top-0 z-10 bg-muted/50 text-[11px] font-medium text-muted-foreground uppercase tracking-wider select-none">
      <tr>
        <th className={thCls} />
        <th className={thCls}>
          <button className="px-1 h-full w-full cursor-pointer hover:text-foreground transition-colors flex items-center whitespace-nowrap" onClick={() => onSort('project')}>
            Project<SortIndicator column="project" sort={sort} />
          </button>
        </th>
        <th className={thCls}><button className={btnCls} onClick={() => onSort('title')}>Title<SortIndicator column="title" sort={sort} /></button></th>
        <th className={thCls}><button className={btnCls} onClick={() => onSort('list')}>List<SortIndicator column="list" sort={sort} /></button></th>
        <th className={thCls}><button className={btnCls} onClick={() => onSort('assignee')}>Assignee<SortIndicator column="assignee" sort={sort} /></button></th>
        <th className={cn(thCls, 'px-2')}>Spec</th>
        <th className={thCls}><button className={btnCls} onClick={() => onSort('activity')}>Activity<SortIndicator column="activity" sort={sort} /></button></th>
        <th className={thCls}><button className={btnCls} onClick={() => onSort('due')}>Due<SortIndicator column="due" sort={sort} /></button></th>
        <th className={cn(thCls, 'px-2')}>Details</th>
      </tr>
    </thead>
  );
}

// ─── Todos panel (TodoCard + scope/status/search + enrichment) ─────────────────

const TODOS_PREVIEW_OPEN_KEY = 'flownotes-todos-preview-open';

export interface TodosPanelProps {
  /** Default status filter (e.g. 'overdue_and_today' for Triage) */
  initialStatusFilter?: StatusFilter;
  /** Default scope (e.g. 'hop_only' for Triage) */
  initialScope?: Scope;
  /** Override section title (e.g. 'Weekly Triage') */
  titleOverride?: string;
  /** Optional: open todo in FlowNotes (same as openTodo) */
  onOpenBasecampTodo?: (projectId: string, todoId: string, title: string, openInNewTab?: boolean) => void;
  /** When true, dispatch triage-context-updated events so the chat pane can see this panel's items. */
  emitViewContext?: boolean;
  /** Workspace organization UUID (scopes assignments API when set). */
  organizationId?: string;
}

export function TodosPanel({
  initialStatusFilter = 'all',
  initialScope,
  titleOverride,
  onOpenBasecampTodo,
  emitViewContext = false,
  organizationId,
}: TodosPanelProps = {}) {
  const { authStatus } = useBasecampAuth(organizationId);
  const { user } = useAuth();
  const meAssignments = useBasecampAssignments(false, organizationId);
  const location = useLocation();
  const navigate = useNavigate();
  const isTriage = Boolean(titleOverride || initialScope);
  const [todosView, setTodosView] = usePersistedState<TodosViewState>({
    key: StorageKeys.BASECAMP_TODOS_VIEW(organizationId),
    defaultValue: migrateLegacyTodosView(),
    schema: StorageSchemas.basecampTodosView,
  });
  const [todosUrlReady, setTodosUrlReady] = useState(false);
  useEffect(() => {
    if (!isBasecampTodosPath(location.pathname)) return;
    const next = todosViewFromSearchParams(new URLSearchParams(location.search));
    if (next) setTodosView(next);
    setTodosUrlReady(true);
  }, [location.pathname, location.search, setTodosView]);
  useEffect(() => {
    if (isTriage || !todosUrlReady) return;
    // Never write Todos filters onto another tab's path (e.g. Weekly still mounted briefly).
    if (!isBasecampTodosPath(location.pathname)) return;
    const next = appendQuery(location.pathname, todosQueryFromView(todosView));
    const current = `${location.pathname}${location.search || ''}`;
    if (next !== current) navigate(next, { replace: true });
  }, [isTriage, todosUrlReady, todosView, location.pathname, location.search, navigate]);
  const [viewMode, setViewMode] = useState<ListViewMode>('list');
  const [triageScope, setTriageScope] = useState<Scope>(initialScope ?? 'all');
  const [triageStatus, setTriageStatus] = useState<StatusFilter>(initialStatusFilter);

  const listMode = todosView.listMode;
  const setListMode = (m: TodosListMode) =>
    setTodosView((v) => ({
      ...v,
      listMode: m,
      assigneeFacet: 'all',
      assigneePerson: null,
    }));
  const scope = isTriage ? triageScope : todosView.scope;
  const setScope = (s: Scope) => {
    if (isTriage) setTriageScope(s);
    else setTodosView((v) => ({ ...v, scope: s }));
  };
  const statusFilter = isTriage ? triageStatus : todosView.statusFilter;
  const setStatusFilter = (s: StatusFilter) => {
    if (isTriage) setTriageStatus(s);
    else setTodosView((v) => ({ ...v, statusFilter: s }));
  };
  const assigneeFacet = todosView.assigneeFacet;
  const assigneePerson = todosView.assigneePerson;
  const setAssignee = (facet: AssigneeFacet, person: string | null) => {
    setTodosView((v) => ({ ...v, assigneeFacet: facet, assigneePerson: person }));
  };
  useEffect(() => {
    if (listMode !== 'me') return;
    if (assigneeFacet === 'all' || assigneeFacet === 'unassigned' || assigneeFacet === 'person') {
      return;
    }
    setTodosView((v) => ({ ...v, assigneeFacet: 'all', assigneePerson: null }));
  }, [listMode, assigneeFacet, setTodosView]);
  const [orgAssignments, setOrgAssignments] = useState<BasecampAssignment[]>([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [orgCachedAt, setOrgCachedAt] = useState<string | null>(null);
  const [orgEmptyReason, setOrgEmptyReason] = useState<string | null>(null);
  const [orgMe, setOrgMe] = useState<MeIdentity | null>(null);
  const {
    searchOpen,
    searchQuery: query,
    setSearchQuery: setQuery,
    inputRef: todosSearchRef,
    close: closeTodosSearch,
    open: openTodosSearch,
  } = usePanelSearch({ initialQuery: todosView.q || '' });
  useEffect(() => {
    const next = todosViewFromSearchParams(new URLSearchParams(location.search));
    if (!next) return;
    setQuery(next.q || '');
  }, [location.search, setQuery]);
  useEffect(() => {
    if (isTriage) return;
    if ((todosView.q || '') === query) return;
    setTodosView((v) => ({ ...v, q: query }));
  }, [isTriage, query, todosView.q, setTodosView]);

  const loadOrgAssignments = useCallback(
    async (refreshPermissions = false) => {
      setOrgLoading(true);
      setOrgError(null);
      try {
        const data = await basecampApi.getOrgAssignments(organizationId, {
          statusFilter: 'all',
          assigneeFacet: 'all',
          refreshPermissions,
        });
        setOrgAssignments(data.assignments || []);
        setOrgCachedAt(data._cached_at || null);
        setOrgEmptyReason(data._empty_reason || null);
        setOrgMe(data._me || null);
      } catch (e) {
        setOrgError(e instanceof Error ? e.message : 'Failed to load org todos');
        setOrgAssignments([]);
        setOrgEmptyReason(null);
        setOrgMe(null);
      } finally {
        setOrgLoading(false);
      }
    },
    [organizationId],
  );

  useEffect(() => {
    if (listMode !== 'org') return;
    if (!authStatus?.authenticated) {
      setOrgAssignments([]);
      setOrgCachedAt(null);
      setOrgLoading(false);
      return;
    }
    void loadOrgAssignments(false);
  }, [listMode, loadOrgAssignments, authStatus?.authenticated]);

  const assignments = listMode === 'org' ? orgAssignments : meAssignments.assignments;
  const isLoading = listMode === 'org' ? orgLoading : meAssignments.isLoading;
  const error = listMode === 'org' ? orgError : meAssignments.error;
  const cachedAt = listMode === 'org' ? orgCachedAt : meAssignments.cachedAt;
  const refetch = useCallback(
    (force = false) => {
      if (listMode === 'org') {
        void loadOrgAssignments(Boolean(force));
      } else {
        meAssignments.refetch(force);
      }
    },
    [listMode, loadOrgAssignments, meAssignments.refetch],
  );
  const [workspecMap, setWorkspecMap] = useState<Record<string, { status: string; filePath: string }>>({});
  const [lastCommentMap, setLastCommentMap] = useState<
    Record<string, { text: string; createdAt: string; creatorName?: string; stale?: boolean }>
  >({});
  const [sort, setSort] = useState<TodoSortState | null>(null);
  const [selectedTodoId, setSelectedTodoId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(() => {
    try {
      return localStorage.getItem(TODOS_PREVIEW_OPEN_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; todoId: string } | null>(null);
  const [refreshingRowId, setRefreshingRowId] = useState<string | null>(null);
  const todosTableRef = useRef<HTMLDivElement>(null);
  const todosLoadMoreRef = useRef<HTMLTableRowElement>(null);
  const [visibleCount, setVisibleCount] = useState(TODOS_PAGE_SIZE);
  const enrichingIdsRef = useRef<Set<string>>(new Set());
  const [enrichingIds, setEnrichingIds] = useState<Set<string>>(() => new Set());

  const mergeWorkspec = useCallback((todoId: string, entry: { status?: string; filePath?: string | null }) => {
    setWorkspecMap((prev) => applyWorkspecMerge(prev, todoId, entry));
  }, []);

  const mergeComment = useCallback(
    (
      todoId: string,
      entry: { content?: string; created_at?: string; creator_name?: string } | null
    ) => {
      setLastCommentMap((prev) => applyCommentMerge(prev, todoId, entry));
    },
    []
  );

  const handleSort = useCallback((col: TodoSortColumn) => {
    setSort(prev => {
      if (prev?.column === col) return prev.dir === 'asc' ? { column: col, dir: 'desc' } : null;
      return { column: col, dir: 'asc' };
    });
  }, []);

  // Per-row refresh: re-fetch workspec + last comment for a single todo without full list reload
  const handleRefreshRow = useCallback(async (todoId: string, options?: { silent?: boolean }) => {
    const todo = (assignments ?? []).find(a => String(a.id) === String(todoId));
    if (!todo) return;
    setRefreshingRowId(todoId);
    const projectId = todo.project?.id ?? '';
    try {
      const response = await fetchWithAuth('/api/todos-enrich-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: todoId, projectId, updatedAt: todo._updated_at ?? '' }],
          force: true,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || 'Failed to refresh row');
      }
      const data = await response.json();
      const ws = data?.workspec?.[todoId];
      const comment = data?.comments?.[todoId];
      if (ws?.status != null) mergeWorkspec(todoId, ws);
      mergeComment(todoId, comment ?? null);
      if (!options?.silent) toast.success('Row refreshed');
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to refresh row';
      if (!options?.silent) toast.error(message);
      console.error('[TodosPanel] Refresh row failed:', e);
    } finally {
      setRefreshingRowId(null);
    }
  }, [assignments, mergeWorkspec, mergeComment]);

  // Toolbar shared refresh: listen for section-level refresh event dispatched by document-viewer.tsx
  useEffect(() => {
    const onSectionRefresh = (e: Event) => {
      const section = (e as CustomEvent).detail?.section;
      if (!section || section === 'todos') refetch(true);
    };
    window.addEventListener('basecamp-section-refresh', onSectionRefresh);
    return () => window.removeEventListener('basecamp-section-refresh', onSectionRefresh);
  }, [refetch]);

  const meIdentity = useMemo(
    () =>
      mergeMeIdentity(
        orgMe || {},
        inferMeFromAssignments(meAssignments.assignments || []),
        {
          email: user?.email ?? null,
          name:
            typeof user?.user_metadata?.full_name === 'string'
              ? user.user_metadata.full_name
              : null,
        },
      ),
    [orgMe, meAssignments.assignments, user?.email, user?.user_metadata?.full_name],
  );

  const byScope = filterByScope(assignments ?? [], scope);
  const byStatus = filterByStatus(byScope, statusFilter);
  const byAssignee = byStatus.filter((a) =>
    rowMatchesAssigneeFacet({
      assignees: a.assignees,
      assignee: a.assignee,
      facet: assigneeFacet,
      personName: assigneePerson,
      orgMode: listMode === 'org',
      meId: listMode === 'org' ? meIdentity.id : null,
      meEmail: listMode === 'org' ? meIdentity.email : null,
      meName: listMode === 'org' ? meIdentity.name : null,
    }),
  );
  const filteredBase = byAssignee.filter((a) => assignmentMatchesBasecampQuery(a, query));

  const peopleOptions = useMemo(() => groupAssigneeNames(byStatus), [byStatus]);

  const filtered = useMemo(() => {
    if (!sort) return filteredBase;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filteredBase].sort((a, b) => {
      let cmp = 0;
      switch (sort.column) {
        case 'project': cmp = (a._project_name ?? a.project?.name ?? '').localeCompare(b._project_name ?? b.project?.name ?? ''); break;
        case 'title': cmp = a.title.localeCompare(b.title); break;
        case 'list': cmp = (a._parent_title ?? '').localeCompare(b._parent_title ?? ''); break;
        case 'assignee': {
          const an = formatAssigneesLabel(a.assignees, a.assignee);
          const bn = formatAssigneesLabel(b.assignees, b.assignee);
          cmp = an.localeCompare(bn);
          break;
        }
        case 'activity': {
          const ac = lastCommentMap[a.id]?.createdAt ?? '';
          const bc = lastCommentMap[b.id]?.createdAt ?? '';
          cmp = ac.localeCompare(bc);
          break;
        }
        case 'due': {
          const ad = a.due_on ?? '';
          const bd = b.due_on ?? '';
          if (!ad && !bd) cmp = 0;
          else if (!ad) cmp = 1;
          else if (!bd) cmp = -1;
          else cmp = ad.localeCompare(bd);
          break;
        }
      }
      return cmp * dir;
    });
  }, [filteredBase, sort, lastCommentMap]);

  const displayRows = useMemo(
    () => buildAssignmentDisplayRows(filtered),
    [filtered],
  );

  useEffect(() => {
    setVisibleCount(TODOS_PAGE_SIZE);
    todosTableRef.current?.scrollTo({ top: 0 });
  }, [listMode, scope, statusFilter, assigneeFacet, assigneePerson, query, sort]);

  const visibleRows = displayRows.slice(0, visibleCount);
  const hiddenRowCount = Math.max(0, displayRows.length - visibleRows.length);

  useEffect(() => {
    if (!selectedTodoId) return;
    const idx = displayRows.findIndex((r) => String(r.id) === selectedTodoId);
    if (idx >= visibleCount) {
      setVisibleCount(idx + 1);
    }
  }, [selectedTodoId, displayRows, visibleCount]);

  useTodosColumnEnrich(
    assignments,
    visibleRows,
    isLoading,
    mergeWorkspec,
    mergeComment,
    enrichingIdsRef,
    setEnrichingIds,
  );

  useEffect(() => {
    if (hiddenRowCount === 0) return;
    const root = todosTableRef.current;
    const target = todosLoadMoreRef.current;
    if (!root || !target) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        setVisibleCount((n) => Math.min(n + TODOS_PAGE_SIZE, displayRows.length));
      },
      { root, rootMargin: '160px' },
    );
    io.observe(target);
    return () => io.disconnect();
  }, [hiddenRowCount, displayRows.length, visibleRows.length]);

  const loadedCount = assignments?.length ?? 0;
  const filterLabels = describeTodosViewFilters(
    { scope, statusFilter, assigneeFacet, assigneePerson },
    query,
  );
  const isFiltered = filterLabels.length > 0;
  const clearTodosFilters = () => {
    setScope('all');
    setStatusFilter('all');
    setAssignee('all', null);
    closeTodosSearch();
  };

  const selectedAssignment = useMemo(
    () =>
      selectedTodoId
        ? displayRows.find((r) => String(r.id) === selectedTodoId) ?? null
        : null,
    [selectedTodoId, displayRows],
  );

  const contextMenuTodo = useMemo(
    () =>
      contextMenu
        ? displayRows.find((r) => String(r.id) === contextMenu.todoId) ?? null
        : null,
    [contextMenu, displayRows],
  );

  useEffect(() => {
    try {
      localStorage.setItem(TODOS_PREVIEW_OPEN_KEY, previewOpen ? 'true' : 'false');
    } catch {
      /* ignore storage errors */
    }
  }, [previewOpen]);

  const togglePreview = useCallback(() => setPreviewOpen((v) => !v), []);
  useRegisterIndexPreviewPin({
    open: previewOpen,
    onToggle: togglePreview,
    testId: testIds.basecamp.todosPreviewToggle,
  });

  useEffect(() => {
    if (!previewOpen || !selectedTodoId) return;
    void handleRefreshRow(selectedTodoId, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh only when preview/selection changes, not when assignments refetch
  }, [previewOpen, selectedTodoId]);

  // Emit triage view context so the chat pane can scope to these items
  useEffect(() => {
    if (!emitViewContext || filtered.length === 0) return;
    const today = todayInLosAngeles();
    const items = filtered.map((a) => {
      const dueOn = a.due_on ?? '';
      const daysOverdue = dueOn && dueOn < today
        ? Math.floor((new Date(today).getTime() - new Date(dueOn).getTime()) / 86400000)
        : 0;
      const ws = workspecMap[a.id];
      return {
        id: a.id,
        title: a.title,
        projectName: a._project_name ?? a.project?.name ?? '',
        projectId: a.project?.id ?? '',
        parentTitle: a._parent_title ?? '',
        dueOn,
        daysOverdue,
        appUrl: a._app_url ?? a.url,
        isHopInternal: isHopInternal(a),
        workspecStatus: ws?.status,
        workspecFilePath: ws?.filePath ?? null,
      };
    });
    window.dispatchEvent(new CustomEvent('triage-context-updated', {
      detail: { scope, items, loadedAt: new Date().toISOString() },
    }));
  }, [emitViewContext, filtered.length, filtered.map((a) => a.id).join(','), scope, workspecMap]);

  // Hydrate Spec + Activity columns from DB-backed fields on assignments (see enrichAssignmentsWithTodoCache).
  useEffect(() => {
    if (!assignments?.length) {
      setWorkspecMap({});
      setLastCommentMap({});
      return;
    }
    const { workspecMap: ws, lastCommentMap: lc } = hydrateMapsFromAssignments(assignments);
    const ids = new Set(assignments.map((x) => String(x.id)));
    setWorkspecMap((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        if (!ids.has(k)) delete next[k];
      }
      for (const [id, v] of Object.entries(ws)) {
        next[id] = v;
      }
      return next;
    });
    setLastCommentMap((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        if (!ids.has(k)) delete next[k];
      }
      for (const [id, v] of Object.entries(lc)) {
        next[id] = v;
      }
      return next;
    });
  }, [assignments]);

  const handleDueDateChange = async (todoId: string, newDate: string) => {
    const a = filtered.find((x) => String(x.id) === String(todoId));
    if (!a) return;
    const projectId = a.project?.id ?? '';
    const apiTodoId =
      a._recording_type === 'Kanban::Step' && a._parent_todo_id
        ? a._parent_todo_id
        : String(a.id);
    const todoUrl = resolveBasecampTodoIngestUrlFromTodo(
      { app_url: a._app_url, url: a.url },
      projectId,
      apiTodoId,
    );
    try {
      const res = await fetchWithAuth('/ai/todo/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          todoUrl,
          updates: { due_on: newDate },
          options: { notify: false },
        }),
      });
      if (res.ok) {
        refetch(true);
        window.dispatchEvent(new CustomEvent('triage-due-date-updated'));
      } else throw new Error((await res.json().catch(() => ({}))).error);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to update due date';
      toast.error(message);
      console.error('Failed to update due date:', e);
    }
  };

  const openTodoHandler = useCallback(
    (a: BasecampAssignment, event?: { metaKey?: boolean; ctrlKey?: boolean }) => {
      if (onOpenBasecampTodo) {
        const projectId = a.project?.id ?? '';
        const openInNewTab = isModClick(event);
        if (projectId) onOpenBasecampTodo(projectId, a.id, a.title, openInNewTab);
        return;
      }
      openTodo(a, organizationId, undefined, isModClick(event));
    },
    [onOpenBasecampTodo, organizationId],
  );

  const handleRowContextMenu = useCallback((todoId: string, e: React.MouseEvent) => {
    e.preventDefault();
    setSelectedTodoId(String(todoId));
    setContextMenu({ x: e.clientX, y: e.clientY, todoId: String(todoId) });
  }, []);

  const selectableRowIds = useMemo(
    () => displayRows.map((r) => String(r.id)),
    [displayRows],
  );

  useEffect(() => {
    if (selectedTodoId && !selectableRowIds.includes(selectedTodoId)) {
      setSelectedTodoId(null);
    }
  }, [selectableRowIds, selectedTodoId]);

  useEffect(() => {
    const el = todosTableRef.current;
    if (!el) return;

    const handleKey = (e: KeyboardEvent) => {
      if (selectableRowIds.length === 0) return;

      if (e.key === 'Escape') {
        setSelectedTodoId(null);
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const currentIdx = selectedTodoId ? selectableRowIds.indexOf(selectedTodoId) : -1;
        const nextIdx =
          e.key === 'ArrowDown'
            ? (currentIdx + 1) % selectableRowIds.length
            : (currentIdx - 1 + selectableRowIds.length) % selectableRowIds.length;
        if (nextIdx >= visibleCount) {
          setVisibleCount((n) => Math.min(Math.max(n, nextIdx + 1), displayRows.length));
        }
        setSelectedTodoId(selectableRowIds[nextIdx] ?? null);
        return;
      }

      if (e.key === 'Enter' && selectedTodoId) {
        e.preventDefault();
        const row = displayRows.find((r) => String(r.id) === selectedTodoId);
        if (row) openTodoHandler(row);
      }
    };

    el.addEventListener('keydown', handleKey);
    return () => el.removeEventListener('keydown', handleKey);
  }, [selectableRowIds, selectedTodoId, displayRows, openTodoHandler, visibleCount]);

  const todosFooter = (
          <ListSurfaceStatusBar
            includePreviewTip
            data-testid={testIds.basecamp.todosStatusBar}
            trailing={
              <span className="truncate">
                {hiddenRowCount > 0
                  ? `Showing ${visibleRows.length} of ${displayRows.length}`
                  : isFiltered
                    ? `${filtered.length} of ${loadedCount} todo${loadedCount === 1 ? '' : 's'}`
                    : `${filtered.length} todo${filtered.length === 1 ? '' : 's'}`}
                {cachedAt ? (
                  <>
                    <span className="mx-1.5 opacity-40" aria-hidden>
                      ·
                    </span>
                    <Clock className="mr-1 inline h-3 w-3" />
                    {new Date(cachedAt).toLocaleString()}
                  </>
                ) : null}
              </span>
            }
          />
  )

  if (!authStatus?.authenticated) {
    return (
      <div className="relative h-full">
        {authStatus ? (
          <ConnectState authUrl={authStatus.auth_url} organizationId={organizationId} />
        ) : (
          <LoadingState label="Checking Basecamp connection…" />
        )}
      </div>
    );
  }

  return (
    <IndexSurfaceShell
      data-testid={testIds.basecamp.todosPanel}
      header={
        titleOverride ? (
          <div className="border-b border-border px-4 py-2">
            <h1 className="text-sm font-semibold">{titleOverride}</h1>
          </div>
        ) : null
      }
      search={
        searchOpen ? (
        <PanelSearchBar
          inputRef={todosSearchRef}
          value={query}
          onChange={setQuery}
          onClose={closeTodosSearch}
          placeholder="filter by title, project, list..."
          resultCount={filtered.length}
          totalCount={loadedCount}
          countNoun="todos"
          data-testid={testIds.basecamp.todosSearch}
        />
        ) : null
      }
      footer={todosFooter}
    >
      <PanelGroup direction="horizontal" className="flex-1 min-h-0">
        <Panel minSize={35} className="flex flex-col min-h-0 overflow-hidden">
          <ExtensionFilterBar
            singleRow
            search=""
            onSearchChange={() => undefined}
            showSearch={false}
            chipStyle="segment"
            chipsTestId={testIds.basecamp.todosListMode}
        chips={[
          {
            id: 'me',
            label: 'Me',
            title: 'Your assigned todos (live Basecamp Reports)',
          },
          {
            id: 'org',
            label: 'Org',
            title: 'Everyone in projects you can access (shared cache)',
          },
        ]}
        activeChipId={listMode}
        onChipChange={(id) => setListMode(id as TodosListMode)}
        trailing={
          <>
            <TodosFiltersMenu
              scope={scope}
              statusFilter={statusFilter}
              extraCount={
                (assigneeFacet !== 'all' ? 1 : 0) + (query.trim() ? 1 : 0)
              }
              onScopeChange={setScope}
              onStatusChange={setStatusFilter}
              onReset={clearTodosFilters}
            />
            <TodosAssigneeCombobox
              facet={assigneeFacet}
              person={assigneePerson}
              people={peopleOptions}
              onChange={setAssignee}
              lens={listMode}
            />
            <span
              className="ml-auto inline-flex min-w-0 items-center gap-2 text-[11px] text-muted-foreground"
              data-testid={testIds.basecamp.todosCoverage}
            >
              {isFiltered ? (
                <span className="min-w-0 truncate">{filterLabels.join(' · ')}</span>
              ) : null}
              <span className="tabular-nums shrink-0">
                {isFiltered
                  ? `${filtered.length} of ${loadedCount}`
                  : `${filtered.length} shown`}
              </span>
              {isFiltered ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-1.5 text-[11px] text-muted-foreground hover:text-foreground"
                  data-testid={testIds.basecamp.todosShowAll}
                  onClick={clearTodosFilters}
                >
                  Clear
                </Button>
              ) : null}
            </span>
            <ListViewModeToggle
              value={viewMode}
              onChange={setViewMode}
              testId={testIds.basecamp.todosViewMode}
            />
          </>
        }
      />
          <div
            ref={todosTableRef}
            tabIndex={0}
            className="relative min-h-0 flex-1 overflow-auto outline-none focus:ring-0"
          >
            {isLoading && !assignments?.length ? (
              <LoadingState label="Loading todos…" />
            ) : authStatus && !authStatus.authenticated ? (
              <ConnectState authUrl={authStatus.auth_url} organizationId={organizationId} />
            ) : error ? (
              <ErrorState message="Failed to load todos" onRetry={() => refetch(true)} />
            ) : filtered.length === 0 ? (
              <EmptyState
                label={
                  query
                    ? 'No matching todos'
                    : listMode === 'org'
                      ? orgEmptyReason === 'no_allowlist'
                        ? 'No Basecamp projects in your membership list yet. Use Refresh membership on Projects.'
                        : orgEmptyReason === 'no_rows'
                          ? 'No org todos in cache for your projects yet. Seed or open projects to populate.'
                          : 'No org todos match these filters.'
                      : 'No todos in this filter'
                }
              />
            ) : viewMode === 'list' ? (
              <table className="w-full table-fixed border-collapse" style={{ minWidth: TODOS_MIN_WIDTH }}>
                <colgroup>
                  <col style={{ width: 2 }} />
                  <col style={{ width: 110 }} />
                  <col />{/* title: auto — fills remaining width */}
                  <col style={{ width: 130 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 200 }} />
                  <col style={{ width: 130 }} />
                  <col style={{ width: 40 }} />
                </colgroup>
                <TodosTableHeader sort={sort} onSort={handleSort} />
                <tbody>
                  {visibleRows.map((a: AssignmentDisplayRow) => (
                    <TodoCard
                      key={`${a._recording_type ?? 'todo'}-${a.id}`}
                      todo={a}
                      rowTestId={testIds.basecamp.todosRow(String(a.id))}
                      isSubtaskRow={a._isSubtaskRow}
                      indent={a._indent}
                      isSelected={selectedTodoId === String(a.id)}
                      onSelect={(todoId) => setSelectedTodoId(String(todoId))}
                      onContextMenu={(e) => handleRowContextMenu(String(a.id), e)}
                      workSpecStatus={workspecMap[a.id] != null ? { status: workspecMap[a.id]!.status, filePath: workspecMap[a.id]!.filePath } : undefined}
                      lastComment={lastCommentMap[a.id] ?? null}
                      isEnriching={enrichingIds.has(String(a.id)) || refreshingRowId === String(a.id)}
                      onDueDateChange={handleDueDateChange}
                      onOpenFlowNotes={(_id, e) => openTodoHandler(a, e)}
                      onOpenBasecamp={(url) => window.open(url, '_blank', 'noopener,noreferrer')}
                      onRefreshRow={handleRefreshRow}
                      onSearch={openTodosSearch}
                      onPreview={() => {
                        setSelectedTodoId(String(a.id));
                        setPreviewOpen(true);
                      }}
                      onPreviewWorkSpec={() => {
                        setSelectedTodoId(String(a.id));
                        setPreviewOpen(true);
                      }}
                      isRefreshing={refreshingRowId === String(a.id)}
                      showDueDateEdit={true}
                      gridTemplate={TODOS_GRID_TEMPLATE}
                    />
                  ))}
                  {hiddenRowCount > 0 ? (
                    <tr ref={todosLoadMoreRef}>
                      <td colSpan={9} className="border-t border-border px-3 py-2 text-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          data-testid={testIds.basecamp.todosShowMore}
                          onClick={() =>
                            setVisibleCount((n) => Math.min(n + TODOS_PAGE_SIZE, displayRows.length))
                          }
                        >
                          Show {Math.min(TODOS_PAGE_SIZE, hiddenRowCount)} more
                          <span className="ml-1.5 text-muted-foreground">
                            ({hiddenRowCount} hidden)
                          </span>
                        </Button>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            ) : (
              <ul className={viewMode === 'grid' ? 'grid grid-cols-2 gap-2 p-3' : 'flex flex-col gap-2 p-3'}>
                {visibleRows.map((a: AssignmentDisplayRow) => (
                  <li key={`${a._recording_type ?? 'todo'}-${a.id}`}>
                    <button
                      type="button"
                      className="w-full rounded-lg border border-border px-3 py-2 text-left hover:bg-accent/40"
                      onClick={() => {
                        setSelectedTodoId(String(a.id));
                        setPreviewOpen(true);
                      }}
                    >
                      <div className="truncate text-sm font-medium">{a.title}</div>
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                        {a._project_name ?? a.project?.name}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Panel>

        {previewOpen && (
          <>
            <SecondaryPreviewResizeHandle />
            <Panel
              defaultSize={32}
              minSize={22}
              maxSize={45}
              className="overflow-hidden flex flex-col min-h-0"
              data-testid={testIds.basecamp.todosSecondary}
            >
              {selectedAssignment ? (
                <BasecampTodoPreviewPane
                  assignment={selectedAssignment}
                  workSpecStatus={workspecMap[selectedAssignment.id] ?? null}
                  lastComment={lastCommentMap[selectedAssignment.id] ?? null}
                  onOpen={(event) => openTodoHandler(selectedAssignment, event)}
                  onClose={() => {
                    setPreviewOpen(false);
                    setSelectedTodoId(null);
                  }}
                  onRefresh={() => handleRefreshRow(String(selectedAssignment.id))}
                  onOpenBasecamp={(url) => window.open(url, '_blank', 'noopener,noreferrer')}
                  isRefreshing={refreshingRowId === String(selectedAssignment.id)}
                />
              ) : (
                <IndexPreviewEmpty
                  noun="todo"
                  row
                  headerTestId={testIds.basecamp.todosPreviewHeader}
                  testId={testIds.basecamp.todosPreviewEmpty}
                  onClose={() => {
                    setPreviewOpen(false);
                    setSelectedTodoId(null);
                  }}
                />
              )}
            </Panel>
          </>
        )}
      </PanelGroup>

      {contextMenu && contextMenuTodo ? (
        <EntityContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          data-testid={testIds.basecamp.todosRowContextMenu}
          onClose={() => setContextMenu(null)}
          entries={buildTodosRowContextMenuEntries({
            todo: contextMenuTodo,
            onPreview: () => setPreviewOpen(true),
            onSearch: openTodosSearch,
            onRefresh: () => handleRefreshRow(String(contextMenuTodo.id)),
            workSpecStatus:
              workspecMap[contextMenuTodo.id] != null
                ? {
                    status: workspecMap[contextMenuTodo.id]!.status,
                    filePath: workspecMap[contextMenuTodo.id]!.filePath,
                  }
                : undefined,
            onViewWorkSpec: () => setPreviewOpen(true),
            close: () => setContextMenu(null),
          })}
        />
      ) : null}
    </IndexSurfaceShell>
  );
}

// ─── Projects panel ───────────────────────────────────────────────────────────

function ProjectsPanel({ organizationId }: { organizationId?: string }) {
  const { projects, isLoading, error, refetch } = useBasecampProjects(organizationId);
  const {
    searchOpen,
    searchQuery: query,
    setSearchQuery,
    inputRef: projectsSearchRef,
    close: closeProjectsSearch,
    open: openProjectsSearch,
  } = usePanelSearch();
  const [viewMode, setViewMode] = useState<ListViewMode>('list');
  const [sortMode, setSortMode] = useState<ProjectsSortMode>(() =>
    loadProjectsSortMode(),
  );
  const [depthFilter, setDepthFilter] =
    useState<ProjectCacheDepthFilter>('all');
  const [revealAll, setRevealAll] = useState(false);
  const setQuery = (value: string) => {
    setSearchQuery(value);
    if (value.trim()) setRevealAll(true);
  };
  const [bookmarkCounts, setBookmarkCounts] = useState<Record<string, number>>(
    {},
  );
  const [touches, setTouches] = useState(() => loadProjectTouches());
  const [previewLoadedMap, setPreviewLoadedMap] = useState(() =>
    loadProjectPreviewLoadedMap(),
  );
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set());
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    loadProjectsPanelUiState(organizationId).selectedId,
  );
  const [previewOpen, setPreviewOpen] = useState(
    () => loadProjectsPanelUiState(organizationId).previewOpen,
  );
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    project: BasecampProject;
  } | null>(null);
  const [previewRemountKey, setPreviewRemountKey] = useState(0);
  const [syncingProjectId, setSyncingProjectId] = useState<string | null>(null);
  const [viewSyncFrame, setViewSyncFrame] = useState<ProjectsViewSyncFrame>('idle');
  /** Session intent: Load/Sync requested for these project ids (survives preview remount). */
  const [previewLoadRequestedIds, setPreviewLoadRequestedIds] = useState<
    Record<string, true>
  >({});
  /** Optimistic Ok after Sync/Load until projects GET returns matching depth. */
  const [listsDepthOverrides, setListsDepthOverrides] = useState<
    Record<string, ListsDepthOverride>
  >({});
  /** Parent-owned in-flight loads (survives preview pane unmount on row switch). */
  const [previewFetchingIds, setPreviewFetchingIds] = useState<
    Record<string, true>
  >({});
  const [, bumpInflightEpoch] = useState(0);

  useEffect(() => subscribeTodoListsInflight(() => bumpInflightEpoch((n) => n + 1)), []);

  // Persist selection so tab Back returns to the same project + open preview.
  // Skip while the list is still loading so a remount cannot wipe session state
  // with selectedId=null / empty validProjects.
  useEffect(() => {
    if (!organizationId) return;
    if (isLoading && selectedId == null) return;
    saveProjectsPanelUiState(organizationId, {
      selectedId,
      previewOpen,
    });
  }, [organizationId, selectedId, previewOpen, isLoading]);

  const prevOrgIdRef = useRef(organizationId);
  useEffect(() => {
    if (!organizationId || organizationId === prevOrgIdRef.current) return;
    prevOrgIdRef.current = organizationId;
    const saved = loadProjectsPanelUiState(organizationId);
    if (saved.selectedId) setSelectedId(saved.selectedId);
    setPreviewOpen(saved.previewOpen);
  }, [organizationId]);

  const validProjects = (projects || []).filter(
    (p): p is NonNullable<typeof p> => p != null && p.id != null,
  ).map((p) => applyListsDepthOverride(p, listsDepthOverrides));

  // Drop chrome timestamps when org lists cache is no longer Ok
  useEffect(() => {
    if (!projects?.length) return;
    const byId = new Map(
      validProjects.map((p) => [String(p.id), p] as const),
    );
    const isListsOk = (id: string) =>
      projectCacheDepth(byId.get(id) ?? { id, name: '', url: '', dock: [] }) ===
      'ok';

    setPreviewLoadedMap((prev) => {
      const next = reconcilePreviewLoadedMap(prev, isListsOk);
      if (Object.keys(next).length !== Object.keys(prev).length) {
        saveProjectPreviewLoadedMap(next);
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- validProjects derived; use projects + overrides
  }, [projects, listsDepthOverrides]);

  // Clear optimistic overrides once projects GET already reports Ok
  useEffect(() => {
    if (!projects?.length) return;
    setListsDepthOverrides((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of Object.keys(prev)) {
        const server = projects.find((p) => p?.id?.toString() === id);
        if (server?.cacheDepth === 'ok') {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [projects]);

  const markListsCacheFresh = useCallback((projectId: string, iso?: string) => {
    const at = iso || new Date().toISOString();
    setListsDepthOverrides((prev) => ({
      ...prev,
      [projectId]: {
        cacheDepth: 'ok',
        listsCachedAt: at,
        listsFresh: true,
      },
    }));
  }, []);

  useEffect(() => {
    saveProjectsSortMode(sortMode);
    if (sortMode !== 'suggested') setRevealAll(false);
  }, [sortMode]);

  // Bookmark counts per project (Maater signal for Suggested ranking)
  useEffect(() => {
    let cancelled = false;
    ;(async () => {
      try {
        const data = await basecampApi.getMyBookmarks(false, organizationId);
        if (cancelled) return;
        const counts: Record<string, number> = {};
        for (const item of data.items || []) {
          const pid = String(
            item.project?.id ??
              (item as { project_id?: string }).project_id ??
              (item as { bucket_id?: string }).bucket_id ??
              '',
          );
          if (!pid) continue;
          counts[pid] = (counts[pid] ?? 0) + 1;
        }
        setBookmarkCounts(counts);
      } catch {
        /* non-critical for ranking */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  // Refresh touches when returning to Projects (opens record via openProject)
  useEffect(() => {
    const refresh = () => setTouches(loadProjectTouches());
    refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);

  const nameFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return validProjects;
    return validProjects.filter((p) =>
      (p.name || '').toLowerCase().includes(q),
    );
  }, [validProjects, query]);

  const depthCounts = useMemo(() => {
    const counts = { all: nameFiltered.length, ok: 0, partial: 0, none: 0 };
    for (const p of nameFiltered) {
      const d = projectCacheDepth(p);
      counts[d] += 1;
    }
    return counts;
  }, [nameFiltered]);

  const depthFiltered = useMemo(() => {
    if (depthFilter === 'all') return nameFiltered;
    return nameFiltered.filter(
      (p) => projectCacheDepth(p) === depthFilter,
    );
  }, [nameFiltered, depthFilter]);

  const ordered = useMemo(
    () =>
      orderProjects(depthFiltered, sortMode, {
        bookmarkCounts,
        touches,
        pinnedIds,
      }),
    [depthFiltered, sortMode, bookmarkCounts, touches, pinnedIds],
  );

  const { visibleProjects, hiddenCount, relevanceById } = useMemo(() => {
    const map = new Map(
      ordered.map((r) => [r.id, { bookmarkCount: r.bookmarkCount }]),
    );
    if (sortMode !== 'suggested' || revealAll || query.trim()) {
      return {
        visibleProjects: ordered.map((r) => r.project),
        hiddenCount: 0,
        relevanceById: map,
      };
    }
    const top = ordered.slice(0, PROJECTS_RELEVANCE_CAP);
    return {
      visibleProjects: top.map((r) => r.project),
      hiddenCount: Math.max(0, ordered.length - top.length),
      relevanceById: map,
    };
  }, [sortMode, ordered, revealAll, query]);

  const pinned = visibleProjects.filter((p) =>
    pinnedIds.has(p.id?.toString() ?? ''),
  );
  const unpinned = visibleProjects.filter(
    (p) => !pinnedIds.has(p.id?.toString() ?? ''),
  );

  useEffect(() => {
    if (
      shouldClearStaleProjectsSelection({
        selectedId,
        projectsReady: !isLoading,
        validIds: validProjects.map((p) => p.id?.toString() ?? '').filter(Boolean),
      })
    ) {
      setSelectedId(null);
    }
  }, [isLoading, validProjects, selectedId]);

  // Restored selection may sit below the Suggested cap - reveal so the row can highlight.
  useEffect(() => {
    if (
      selectedId &&
      sortMode === 'suggested' &&
      !revealAll &&
      !query.trim() &&
      !visibleProjects.some((p) => p.id?.toString() === selectedId) &&
      validProjects.some((p) => p.id?.toString() === selectedId)
    ) {
      setRevealAll(true);
    }
  }, [
    selectedId,
    sortMode,
    revealAll,
    query,
    visibleProjects,
    validProjects,
  ]);

  const selectedProject =
    validProjects.find((p) => p.id?.toString() === selectedId) ?? null;

  const togglePin = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setPinnedIds((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const formatLastSync = (lastSync?: Date) => {
    if (!lastSync) return 'Never synced';
    const diff = Date.now() - new Date(lastSync).getTime();
    const h = Math.floor(diff / 3_600_000);
    if (h < 1) return 'Just now';
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  const syncLabelForProject = (project: BasecampProject) => {
    const id = project.id?.toString() ?? '';
    const depth = projectCacheDepth(project);
    const previewIso = id ? previewLoadedMap[id] : null;
    if (syncingProjectId === id || previewFetchingIds[id] || isTodoListsFetchInflight(id)) {
      return 'Loading...';
    }
    if (depth === 'ok' && (previewIso || project.listsCachedAt)) {
      return `Cached ${formatRelativeSyncLabel(previewIso || project.listsCachedAt)}`;
    }
    if (depth === 'ok') {
      return 'Cached in workspace';
    }
    if (depth === 'partial' && project.listsCachedAt) {
      return `Stale · ${formatRelativeSyncLabel(project.listsCachedAt)}`;
    }
    if (project.lastSync) {
      return `Last sync: ${formatLastSync(project.lastSync)}`;
    }
    return 'Never loaded';
  };

  const syncProjectTodos = useCallback(
    async (project: BasecampProject) => {
      const id = project.id?.toString() ?? '';
      if (!id) return;
      setSelectedId(id);
      setPreviewOpen(true);
      try {
        localStorage.setItem(PROJECTS_PREVIEW_OPEN_KEY, 'true');
      } catch {
        /* ignore */
      }
      setPreviewLoadRequestedIds((prev) => ({ ...prev, [id]: true }));
      setSyncingProjectId(id);
      try {
        await basecampApi.getTodoLists(id, true);
        const iso = new Date().toISOString();
        recordProjectPreviewLoaded(id);
        setPreviewLoadedMap((prev) => ({ ...prev, [id]: iso }));
        markListsCacheFresh(id, iso);
        setPreviewRemountKey((n) => n + 1);
        void refetch();
        toast.success('Project todos synced', {
          description: project.name,
        });
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : 'Failed to sync project todos',
        );
      } finally {
        setSyncingProjectId(null);
      }
    },
    [refetch, markListsCacheFresh],
  );

  const viewBusy = viewSyncFrame === 'refreshing-visible' || Boolean(syncingProjectId);

  const refreshVisibleCaches = useCallback(async () => {
    if (viewBusy) return;
    const targets = visibleProjects.filter((p) => p.id != null);
    if (targets.length === 0) return;
    setViewSyncFrame('refreshing-visible');
    let failed = 0;
    try {
      for (const project of targets) {
        const id = project.id!.toString();
        try {
          await basecampApi.getTodoLists(id, true);
          const iso = new Date().toISOString();
          recordProjectPreviewLoaded(id);
          setPreviewLoadedMap((prev) => ({ ...prev, [id]: iso }));
          setPreviewLoadRequestedIds((prev) => ({ ...prev, [id]: true }));
          markListsCacheFresh(id, iso);
        } catch {
          failed += 1;
        }
      }
      setPreviewRemountKey((n) => n + 1);
      void refetch();
      if (failed === 0) {
        toast.success('Visible projects refreshed', {
          description: `${targets.length} project${targets.length === 1 ? '' : 's'}`,
        });
      } else {
        toast.error(
          `Refreshed ${targets.length - failed} of ${targets.length}; ${failed} failed`,
        );
      }
      setViewSyncFrame('done');
      window.setTimeout(() => setViewSyncFrame('idle'), 1800);
    } catch (e) {
      setViewSyncFrame('idle');
      toast.error(e instanceof Error ? e.message : 'Failed to refresh visible projects');
    }
  }, [viewBusy, visibleProjects, refetch, markListsCacheFresh]);

  const reloadProjectList = useCallback(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    const onAction = (e: Event) => {
      const action = indexViewActionFromEvent(e);
      if (action === 'reload-list') reloadProjectList();
      if (action === 'refresh-membership') void refetch(true);
      if (action === 'refresh-visible') void refreshVisibleCaches();
    };
    window.addEventListener(INDEX_VIEW_ACTION_EVENT, onAction);
    return () => window.removeEventListener(INDEX_VIEW_ACTION_EVENT, onAction);
  }, [reloadProjectList, refetch, refreshVisibleCaches]);

  useEffect(() => {
    const onRefresh = (e: Event) => {
      const section = (e as CustomEvent<{ section?: string }>).detail?.section;
      if (section === 'projects') reloadProjectList();
    };
    window.addEventListener('basecamp-section-refresh', onRefresh);
    return () => window.removeEventListener('basecamp-section-refresh', onRefresh);
  }, [reloadProjectList]);

  const statusBarText = (() => {
    if (viewSyncFrame === 'refreshing-visible') {
      return 'Refreshing cache for visible projects…';
    }
    if (syncingProjectId) {
      const name =
        validProjects.find((p) => p.id?.toString() === syncingProjectId)?.name ??
        'project';
      return `Refreshing ${name}…`;
    }
    if (viewSyncFrame === 'done') return 'Cache updated · just now';
    if (isLoading) return 'Loading projects…';
    return 'Idle · pull on demand';
  })();

  const requestPreviewLoad = useCallback((projectId: string) => {
    if (!projectId) return;
    setPreviewLoadRequestedIds((prev) =>
      prev[projectId] ? prev : { ...prev, [projectId]: true },
    );
    setPreviewFetchingIds((prev) =>
      prev[projectId] ? prev : { ...prev, [projectId]: true },
    );
    // Load CTA = explicit Basecamp walk (not a stale re-read)
    void ensureTodoListsCached(projectId, true)
      .then((cache) => {
        const iso = cache.cachedAt || new Date().toISOString();
        recordProjectPreviewLoaded(projectId);
        setPreviewLoadedMap((prev) => ({ ...prev, [projectId]: iso }));
        markListsCacheFresh(projectId, iso);
        setPreviewRemountKey((n) => n + 1);
        void refetch();
      })
      .catch((e) => {
        toast.error(
          e instanceof Error ? e.message : 'Failed to load project preview',
        );
      })
      .finally(() => {
        setPreviewFetchingIds((prev) => {
          if (!prev[projectId]) return prev;
          const next = { ...prev };
          delete next[projectId];
          return next;
        });
      });
  }, [markListsCacheFresh, refetch]);

  const togglePreview = useCallback(() => {
    setPreviewOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PROJECTS_PREVIEW_OPEN_KEY, String(next));
      } catch {
        /* ignore */
      }
      if (next && !selectedId && visibleProjects[0]) {
        setSelectedId(visibleProjects[0].id?.toString() ?? null);
      }
      return next;
    });
  }, [selectedId, visibleProjects]);

  useRegisterIndexPreviewPin({
    open: previewOpen,
    onToggle: togglePreview,
    testId: testIds.basecamp.projectsPreviewToggle,
  });

  const renderProjectRow = (project: BasecampProject) => {
    const id = project.id?.toString() ?? '';
    const isPinned = pinnedIds.has(id);
    const depth = projectCacheDepth(project);
    const rowBusy =
      syncingProjectId === id ||
      Boolean(previewFetchingIds[id]) ||
      isTodoListsFetchInflight(id);
    const bookmarkCount = relevanceById.get(id)?.bookmarkCount ?? 0;
    return (
      <li key={id}>
        <ExtensionListRow
          selected={selectedId === id}
          testId={testIds.basecamp.projectsRow(id)}
          onSelect={() => setSelectedId(id)}
          onOpen={() => openProject(project, organizationId)}
          onContextMenu={(e) => {
            setSelectedId(id);
            setContextMenu({ x: e.clientX, y: e.clientY, project });
          }}
          trailing={
            <div className="flex shrink-0 items-center gap-1">
              <ProjectCacheDepthBadge depth={depth} busy={rowBusy} />
              {isPinned ? <Pin className="h-3 w-3 text-yellow-500" /> : null}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void syncProjectTodos(project);
                }}
                disabled={viewBusy}
                className="rounded p-1 opacity-0 transition-opacity hover:bg-accent-foreground/10 group-hover:opacity-100 disabled:opacity-40"
                title="Refresh cache for this project"
                data-testid={testIds.basecamp.projectsRowRefresh(id)}
              >
                <RefreshCw
                  className={cn('h-3 w-3 text-muted-foreground', rowBusy && 'animate-spin')}
                />
              </button>
              <button
                type="button"
                onClick={(e) => togglePin(id, e)}
                className="rounded p-1 opacity-0 transition-opacity hover:bg-accent-foreground/10 group-hover:opacity-100"
                title={isPinned ? 'Unpin' : 'Pin'}
              >
                <Pin className="h-3 w-3 text-muted-foreground" />
              </button>
            </div>
          }
          className="items-center"
        >
          <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              <span className="truncate">{project.name}</span>
            </div>
            <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
              {bookmarkCount > 0 ? (
                <button
                  type="button"
                  data-row-action
                  onClick={(e) => {
                    e.stopPropagation();
                    openBookmarksSection(organizationId, {
                      id,
                      name: project.name,
                    });
                  }}
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border/70 bg-muted/50 px-1.5 py-0 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label={`Open ${bookmarkCount} bookmark${bookmarkCount === 1 ? '' : 's'} in ${project.name}`}
                  data-testid={testIds.basecamp.projectsRowBookmarks(id)}
                >
                  <Bookmark className="h-3 w-3" />
                  {bookmarkCount}
                </button>
              ) : null}
              {bookmarkCount > 0 ? (
                <span className="shrink-0 opacity-40" aria-hidden>
                  ·
                </span>
              ) : null}
              <span className="truncate">{syncLabelForProject(project)}</span>
            </div>
          </div>
        </ExtensionListRow>
      </li>
    );
  };

  const countLabel =
    sortMode === 'suggested' && hiddenCount > 0 && !query.trim()
      ? `${visibleProjects.length} suggested · ${hiddenCount} lower relevance hidden`
      : `${visibleProjects.length} shown`;

  const projectsFiltered = depthFilter !== 'all' || sortMode !== 'suggested' || Boolean(query.trim())
  const clearProjectsFilters = () => {
    setDepthFilter('all')
    setSortMode('suggested')
    setQuery('')
    setRevealAll(false)
  }
  const projectsFooter = (
          <ListSurfaceStatusBar
            includePreviewTip
            data-testid={testIds.basecamp.projectsStatusBar}
            helpTestId={testIds.basecamp.projectsStatusInfo}
            tips={`${PROJECTS_MEMBERSHIP_HELP} ${LIST_GESTURE_TIPS_WITH_PREVIEW}`}
            leading={
              (viewSyncFrame === 'refreshing-visible' || syncingProjectId || isLoading) ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
              ) : null
            }
            trailing={
              <>
                <span
                  className="truncate"
                  data-testid={testIds.basecamp.projectsStatusText}
                >
                  {statusBarText}
                </span>
                <span className="text-muted-foreground/50" aria-hidden>
                  ·
                </span>
                <span className="shrink-0 tabular-nums">{countLabel}</span>
              </>
            }
          />
  )

  if (error?.status === 401) {
    return (
      <div className="relative h-full">
        <ConnectState authUrl={error.auth_url} organizationId={organizationId} />
      </div>
    );
  }

  return (
    <IndexSurfaceShell
      data-testid={testIds.basecamp.projectsPanel}
      search={
        searchOpen ? (
        <PanelSearchBar
          inputRef={projectsSearchRef}
          value={query}
          onChange={setQuery}
          onClose={closeProjectsSearch}
          placeholder="filter by project name..."
          resultCount={visibleProjects.length}
          totalCount={validProjects.length}
          countNoun="projects"
          data-testid={testIds.basecamp.projectsSearch}
        />
        ) : null
      }
      footer={projectsFooter}
    >
      <PanelGroup direction="horizontal" className="flex min-h-0 flex-1">
        <Panel minSize={35} className="flex min-h-0 min-w-0 flex-col overflow-hidden">
          <ExtensionFilterBar
            singleRow
            search=""
            onSearchChange={() => undefined}
            showSearch={false}
            trailing={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1 text-xs font-normal"
                  aria-label="More filters"
                  data-testid={testIds.basecamp.projectsSort}
                >
                  <SlidersHorizontal className="h-3 w-3" />
                  Filters
                  {projectsFiltered ? (
                    <span className="rounded-full bg-primary px-1 text-[10px] leading-4 text-primary-foreground">
                      {(depthFilter !== 'all' ? 1 : 0) + (sortMode !== 'suggested' ? 1 : 0)}
                    </span>
                  ) : null}
                  <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
              >
                <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Sort by
                </DropdownMenuLabel>
                {PROJECTS_SORT_MODES.map((mode) => (
                  <DropdownMenuItem
                    key={mode}
                    onClick={() => {
                      setSortMode(mode);
                      setRevealAll(false);
                    }}
                    data-testid={testIds.basecamp.projectsSortOption(mode)}
                  >
                    <span className="flex-1">{PROJECTS_SORT_LABELS[mode]}</span>
                    {sortMode === mode ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : null}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Cache depth
                </DropdownMenuLabel>
                <div className="px-1 pb-1">
                  <CacheDepthFilterTrack
                    value={depthFilter}
                    onChange={setDepthFilter}
                    counts={depthCounts}
                  />
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <span className="inline-flex min-w-0 items-center gap-2 text-[11px] text-muted-foreground">
              <span className="tabular-nums shrink-0">{countLabel}</span>
              {projectsFiltered ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-1.5 text-[11px] text-muted-foreground hover:text-foreground"
                  onClick={clearProjectsFilters}
                >
                  Clear
                </Button>
              ) : null}
            </span>
            <ListViewModeToggle
              value={viewMode}
              onChange={setViewMode}
              testId={testIds.basecamp.projectsViewMode}
            />
          </>
        }
      />
          <div className="relative min-h-0 flex-1 overflow-auto">
            {isLoading && !validProjects.length ? (
              <LoadingState label="Loading projects..." />
            ) : error?.status === 401 ? (
              <ConnectState authUrl={error.auth_url} organizationId={organizationId} />
            ) : error ? (
              <ErrorState
                message={error.message || 'Failed to load projects'}
                onRetry={() => refetch()}
              />
            ) : visibleProjects.length === 0 ? (
              <EmptyState
                label={
                  query
                    ? 'No matching projects'
                    : depthFilter !== 'all'
                      ? `No ${depthFilter} projects`
                      : 'No projects found'
                }
                sub={
                  query
                    ? 'Clear the search filter to see all projects.'
                    : depthFilter !== 'all'
                      ? 'Try All, or load previews to change cache depth.'
                      : undefined
                }
              />
            ) : (
              <ul className={viewMode === 'grid' ? 'grid grid-cols-2 gap-2 p-3' : viewMode === 'card' ? 'flex flex-col gap-2 p-3' : undefined}>
                {pinned.length > 0 ? (
                  <>
                    <li className="px-6 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted/30">
                      Pinned
                    </li>
                    {pinned.map(renderProjectRow)}
                    <li className="px-6 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted/30">
                      {sortMode === 'suggested'
                        ? 'Suggested'
                        : PROJECTS_SORT_LABELS[sortMode]}
                    </li>
                  </>
                ) : null}
                {unpinned.map(renderProjectRow)}
                {sortMode === 'suggested' && hiddenCount > 0 && !revealAll ? (
                  <li className="px-6 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-full justify-center text-xs text-muted-foreground"
                      onClick={() => setRevealAll(true)}
                    >
                      Show {hiddenCount} more projects
                    </Button>
                  </li>
                ) : null}
                {sortMode === 'suggested' &&
                revealAll &&
                !query.trim() &&
                ordered.length > PROJECTS_RELEVANCE_CAP ? (
                  <li className="px-6 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-full justify-center text-xs text-muted-foreground"
                      onClick={() => setRevealAll(false)}
                    >
                      Show top {PROJECTS_RELEVANCE_CAP} only
                    </Button>
                  </li>
                ) : null}
              </ul>
            )}
          </div>
        </Panel>

        {previewOpen ? (
          <>
            <SecondaryPreviewResizeHandle />
            <Panel
              defaultSize={32}
              minSize={22}
              maxSize={45}
              className="flex min-h-0 flex-col overflow-hidden"
              data-testid={testIds.basecamp.projectsSecondary}
            >
              {selectedProject ? (
                <BasecampProjectPreviewPane
                  key={`${selectedProject.id}-${previewRemountKey}`}
                  project={selectedProject}
                  organizationId={organizationId}
                  onOpen={(event) => {
                    if (isModClick(event)) {
                      window.dispatchEvent(
                        new CustomEvent('integration-view-change', {
                          detail: {
                            tabId: `basecamp-project-${selectedProject.id}`,
                            title: selectedProject.name,
                            type: 'integration-basecamp',
                            openInNewTab: true,
                            integrationData: {
                              integrationId: 'basecamp',
                              projectId: selectedProject.id?.toString(),
                              projectName: selectedProject.name,
                              ...(organizationId ? { organizationId } : {}),
                            },
                          },
                        })
                      );
                    } else {
                      openProject(selectedProject, organizationId);
                    }
                  }}
                  onClose={() => setPreviewOpen(false)}
                  loadRequested={Boolean(
                    previewLoadRequestedIds[selectedProject.id?.toString() ?? ''],
                  )}
                  isFetching={Boolean(
                    previewFetchingIds[selectedProject.id?.toString() ?? ''] ||
                      isTodoListsFetchInflight(
                        selectedProject.id?.toString() ?? '',
                      ),
                  )}
                  onRequestLoad={() =>
                    requestPreviewLoad(selectedProject.id?.toString() ?? '')
                  }
                  onPreviewLoaded={(projectId, loadedAtIso) => {
                    setPreviewLoadedMap((prev) => ({
                      ...prev,
                      [projectId]: loadedAtIso,
                    }));
                    setPreviewLoadRequestedIds((prev) => ({
                      ...prev,
                      [projectId]: true,
                    }));
                    setPreviewFetchingIds((prev) => {
                      if (!prev[projectId]) return prev;
                      const next = { ...prev };
                      delete next[projectId];
                      return next;
                    });
                  }}
                />
              ) : (
                <IndexPreviewEmpty
                  noun="project"
                  row
                  headerTestId={testIds.basecamp.projectsPreviewHeader}
                  testId={testIds.basecamp.projectsPreviewEmpty}
                  onClose={() => setPreviewOpen(false)}
                />
              )}
            </Panel>
          </>
        ) : null}
      </PanelGroup>

      {contextMenu ? (
        <EntityContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          data-testid={testIds.basecamp.projectsRowContextMenu}
          onClose={() => setContextMenu(null)}
          entries={[
            {
              label: 'Preview',
              icon: Eye,
              onSelect: () => {
                setSelectedId(contextMenu.project.id?.toString() ?? null);
                setPreviewOpen(true);
                try {
                  localStorage.setItem(PROJECTS_PREVIEW_OPEN_KEY, 'true');
                } catch {
                  /* ignore */
                }
              },
            },
            {
              label: 'Open',
              icon: AppWindow,
              onSelect: () => openProject(contextMenu.project, organizationId),
            },
            {
              label: 'Refresh cache',
              icon: RefreshCw,
              disabled: viewBusy,
              onSelect: () => {
                void syncProjectTodos(contextMenu.project);
              },
            },
            ...(contextMenu.project.app_url || contextMenu.project.url
              ? [
                  {
                    separator: true as const,
                  },
                  {
                    label: 'Open in Basecamp',
                    icon: ExternalLink,
                    onSelect: () => {
                      const raw =
                        contextMenu.project.app_url || contextMenu.project.url;
                      const web = raw
                        .replace('basecampapi.com', 'basecamp.com')
                        .replace(/\.json$/, '');
                      window.open(web, '_blank', 'noopener,noreferrer');
                    },
                  },
                ]
              : []),
            { separator: true as const },
            panelSearchMenuAction(openProjectsSearch),
          ]}
        />
      ) : null}
    </IndexSurfaceShell>
  );
}

// ─── Router ────────────────────────────────────────────────────────────────────

export function BasecampSectionView({ section, organizationId }: BasecampSectionViewProps) {
  switch (section) {
    case 'bookmarks': return <BookmarksPanel organizationId={organizationId} />;
    case 'mystuff':   return <MyStuffPanel organizationId={organizationId} />;
    case 'todos':     return <TodosPanel organizationId={organizationId} emitViewContext={true} />;
    case 'projects':  return <ProjectsPanel organizationId={organizationId} />;
  }
}
